import { getDossier as cacheGet, setDossier as cacheSet } from './aiCache.js';

/**
 * KI-Dossier zu einem Flugzeug laden — die teure, web-gestützte Übersicht
 * (Opus 4.8 + Websuche im Backend). Ergebnisse sind an den ICAO-Hex gebunden und
 * dreifach gecacht (Speicher → IndexedDB → DynamoDB), sodass jedes Airframe die
 * Modellabfrage nur EINMAL auslöst — geräte- und nutzerübergreifend.
 *
 * Gleiches Race-/Cache-Muster wie data/enrichment.js: ein hex→Promise-Cache im
 * Speicher verhindert Doppelabfragen; das Info-Panel schützt zusätzlich per
 * enrichSeq gegen veraltete Antworten nach Auswahlwechsel.
 */

const AI_BASE = '/ai';
const inflight = new Map(); // hex -> Promise<dossier|null>

// Das Dossier wird serverseitig ASYNC erzeugt (Opus 4.8 + Websuche dauert oft
// länger als der Amplify-Proxy wartet → 504). Das Backend antwortet dann mit
// { status:'pending' } bzw. der Proxy mit 504; wir pollen, bis { status:'ready' }
// kommt (der Hintergrund-Lauf schreibt das Ergebnis in den geteilten Cache).
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_MS = 240000; // ~4 min — danach gibt der Client auf (Ergebnis landet
                            // trotzdem im Cache → nächster Aufruf ist sofort da)

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {Object} flight
 * @returns {Promise<Object|null>} Dossier-Envelope (siehe Backend) oder null
 */
export function fetchDossier(flight) {
  const hex = (flight?.id || '').trim().toLowerCase();
  if (!hex || hex.startsWith('demo')) return Promise.resolve(null);
  if (inflight.has(hex)) return inflight.get(hex);

  const payload = JSON.stringify({
    hex,
    callsign: flight.callsign || null,
    registration: flight.registration || null,
    type: flight.aircraftType || null,
    category: flight.category || null,
    squawk: flight.squawk || null
  });

  // Ein Poll-Schritt: POST /dossier. 504/502/503 = „läuft noch" (Proxy-Timeout
  // während der Hintergrund-Generierung) → als 'pending' behandeln, weiterpollen.
  const poll = async () => {
    const res = await fetch(`${AI_BASE}/dossier`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: payload
    });
    if (res.status === 504 || res.status === 502 || res.status === 503) return { status: 'pending' };
    if (!res.ok) throw new Error(`dossier ${res.status}`);
    return res.json();
  };

  const promise = (async () => {
    const cached = await cacheGet(hex);
    if (cached) return cached;

    const deadline = Date.now() + MAX_POLL_MS;
    let first = true;
    while (Date.now() < deadline) {
      const json = await poll();
      if (json?.status === 'ready' && json?.dossier) {
        await cacheSet(hex, json.dossier);
        return json.dossier;
      }
      if (json?.status === 'error') throw new Error(json.error || 'dossier error');
      // 'pending' → kurz warten und erneut versuchen (erster Poll schnell nach).
      await sleep(first ? 2000 : POLL_INTERVAL_MS);
      first = false;
    }
    throw new Error('dossier timeout');
  })().catch((err) => {
    inflight.delete(hex); // transienter Fehler → späterer Retry möglich
    throw err;            // Panel zeigt einen Fehlerzustand
  });

  inflight.set(hex, promise);
  return promise;
}

/**
 * Bereits gecachtes Dossier (Speicher/IndexedDB) ohne Netzaufruf — für die
 * Sofort-Anzeige beim Öffnen der KI-Seite.
 * @returns {Promise<Object|null>}
 */
export function peekDossier(hex) {
  const key = (hex || '').trim().toLowerCase();
  if (!key) return Promise.resolve(null);
  return cacheGet(key);
}
